import React, { useState, useEffect, useMemo } from 'react';
import { promotionManagementService, Promotion, ListPromotionsParams, PromotionPerformance } from '../services/promotionManagement';
import { ZipPromotionsTab } from './ZipPromotionsTab';
import { PromoCodesTab } from './PromoCodesTab';
import { Table, TableColumn } from '../components/Table';
import './PromotionsPage.css';
import './ZipPromotionsPage.css';
import './PromoCodesPage.css';

type Tab = 'provider' | 'zip' | 'promo';

export const PromotionsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('tab');
    if (t === 'promo' || t === 'zip') return t;
    return 'provider';
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.set('tab', activeTab);
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
  }, [activeTab]);
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<string | undefined>();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState<Promotion | null>(null);
  const [viewingPerformance, setViewingPerformance] = useState<string | null>(null);
  const itemsPerPage = 50;

  useEffect(() => {
    loadPromotions();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadPromotions reads from state
  }, [currentPage, statusFilter, typeFilter, searchTerm]);

  const loadPromotions = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: ListPromotionsParams = {
        limit: itemsPerPage,
        search: searchTerm || undefined,
        isActive: statusFilter !== 'all' ? statusFilter === 'active' : undefined,
        promotionType: typeFilter !== 'all' ? typeFilter : undefined,
      };

      if (currentPage > 1 && lastEvaluatedKey) {
        params.lastEvaluatedKey = lastEvaluatedKey;
      }

      const response = await promotionManagementService.listPromotions(params);
      setPromotions(response.promotions);
      setTotalCount(response.count);
      setLastEvaluatedKey(
        response.lastEvaluatedKey
          ? encodeURIComponent(JSON.stringify(response.lastEvaluatedKey))
          : undefined
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load promotions');
      console.error('Error loading promotions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    setLastEvaluatedKey(undefined);
    loadPromotions();
  };

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
    setCurrentPage(1);
    setLastEvaluatedKey(undefined);
  };

  const handleTypeFilter = (type: string) => {
    setTypeFilter(type);
    setCurrentPage(1);
    setLastEvaluatedKey(undefined);
  };

  const handleDelete = async (promotionId: string) => {
    if (!window.confirm('Are you sure you want to deactivate this promotion?')) {
      return;
    }

    try {
      await promotionManagementService.deletePromotion(promotionId);
      await loadPromotions();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to delete promotion');
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getPromotionStatus = (promotion: Promotion): string => {
    const now = new Date();
    const startDate = new Date(promotion.startDate);
    const endDate = new Date(promotion.endDate);

    if (!promotion.isActive) return 'inactive';
    if (now < startDate) return 'upcoming';
    if (now > endDate) return 'expired';
    return 'active';
  };

  const columns: TableColumn<Promotion>[] = useMemo(() => [
    {
      header: 'Title',
      accessor: 'title',
      render: (value) => (
        <div className="promotion-title">{value as string}</div>
      ),
    },
    {
      header: 'Type',
      accessor: 'promotionType',
      render: (value) => (
        <span className={`promotion-type-badge ${value as string}`}>
          {(value as string).replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
        </span>
      ),
    },
    {
      header: 'Provider',
      accessor: (row) => row.providerId ? (row.providerId as string).substring(0, 8) + '...' : 'All Providers',
      hideOnTablet: true,
    },
    {
      header: 'Value',
      accessor: (row) => {
        if (row.promotionType === 'commission_discount' && row.discountPercentage) {
          return `${row.discountPercentage}%`;
        }
        if ((row.promotionType === 'bonus_payout' || row.promotionType === 'fixed_bonus') && row.bonusAmount) {
          return `₹${row.bonusAmount}`;
        }
        return '-';
      },
    },
    {
      header: 'Status',
      accessor: (row) => getPromotionStatus(row),
      render: (value) => (
        <span className={`status-badge ${value as string}`}>
          {value as string}
        </span>
      ),
    },
    {
      header: 'Start Date',
      accessor: 'startDate',
      render: (value) => formatDate(value as string | undefined),
      hideOnTablet: true,
    },
    {
      header: 'End Date',
      accessor: 'endDate',
      render: (value) => formatDate(value as string | undefined),
      hideOnMobile: true,
    },
    {
      header: 'Actions',
      accessor: (row) => (
        <div className="action-buttons">
          <button
            className="view-button"
            onClick={() => setViewingPerformance(row.promotionId)}
          >
            Performance
          </button>
          <button
            className="edit-button"
            onClick={() => setEditingPromotion(row)}
          >
            Edit
          </button>
          <button
            className="delete-button"
            onClick={() => handleDelete(row.promotionId)}
          >
            Delete
          </button>
        </div>
      ),
      dataLabel: '',
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps -- handleDelete is stable
  ], []);

  return (
    <div className="promotions-page">
      <div className="promotions-header">
        <h1>Promotion Management</h1>
        <div className="promotions-tabs">
          <button
            className={`tab-button ${activeTab === 'provider' ? 'active' : ''}`}
            onClick={() => setActiveTab('provider')}
          >
            Provider Promotions
          </button>
          <button
            className={`tab-button ${activeTab === 'zip' ? 'active' : ''}`}
            onClick={() => setActiveTab('zip')}
          >
            Zip Promotions (Use 10, Get 1 Free)
          </button>
          <button
            className={`tab-button ${activeTab === 'promo' ? 'active' : ''}`}
            onClick={() => setActiveTab('promo')}
          >
            Promo Codes
          </button>
        </div>
        {activeTab === 'provider' && (
          <div className="promotions-actions">
            <button onClick={() => setShowCreateModal(true)} className="create-button">
              Create Promotion
            </button>
          </div>
        )}
      </div>

      {activeTab === 'zip' ? (
        <ZipPromotionsTab />
      ) : activeTab === 'promo' ? (
        <PromoCodesTab />
      ) : (
        <>

      <div className="promotions-filters">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search by title or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="search-button">
            Search
          </button>
        </form>

        <div className="filter-buttons">
          <div className="filter-group">
            <label>Status:</label>
            <button
              className={`filter-button ${statusFilter === 'all' ? 'active' : ''}`}
              onClick={() => handleStatusFilter('all')}
            >
              All
            </button>
            <button
              className={`filter-button ${statusFilter === 'active' ? 'active' : ''}`}
              onClick={() => handleStatusFilter('active')}
            >
              Active
            </button>
            <button
              className={`filter-button ${statusFilter === 'inactive' ? 'active' : ''}`}
              onClick={() => handleStatusFilter('inactive')}
            >
              Inactive
            </button>
          </div>
          <div className="filter-group">
            <label>Type:</label>
            <button
              className={`filter-button ${typeFilter === 'all' ? 'active' : ''}`}
              onClick={() => handleTypeFilter('all')}
            >
              All
            </button>
            <button
              className={`filter-button ${typeFilter === 'commission_discount' ? 'active' : ''}`}
              onClick={() => handleTypeFilter('commission_discount')}
            >
              Commission Discount
            </button>
            <button
              className={`filter-button ${typeFilter === 'bonus_payout' ? 'active' : ''}`}
              onClick={() => handleTypeFilter('bonus_payout')}
            >
              Bonus Payout
            </button>
            <button
              className={`filter-button ${typeFilter === 'fixed_bonus' ? 'active' : ''}`}
              onClick={() => handleTypeFilter('fixed_bonus')}
            >
              Fixed Bonus
            </button>
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">Loading promotions...</div>
      ) : (
        <>
          <div className="promotions-stats">
            <div className="stat-card">
              <div className="stat-value">{totalCount}</div>
              <div className="stat-label">Total Promotions</div>
            </div>
          </div>

          <Table
            columns={columns}
            data={promotions}
            getRowId={(row) => row.promotionId}
            emptyMessage="No promotions found"
            containerClassName="promotions-table-container"
            className="promotions-table"
          />

          <div className="pagination">
            <button
              disabled={currentPage === 1}
              onClick={() => {
                setCurrentPage(currentPage - 1);
                setLastEvaluatedKey(undefined);
              }}
            >
              Previous
            </button>
            <span>Page {currentPage}</span>
            <button
              disabled={!lastEvaluatedKey || promotions.length < itemsPerPage}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}

      {showCreateModal && (
        <CreatePromotionModal
          onClose={async () => {
            setShowCreateModal(false);
            await loadPromotions();
          }}
        />
      )}

      {editingPromotion && (
        <EditPromotionModal
          promotion={editingPromotion}
          onClose={async () => {
            setEditingPromotion(null);
            await loadPromotions();
          }}
        />
      )}

      {viewingPerformance && (
        <PromotionPerformanceModal
          promotionId={viewingPerformance}
          onClose={() => setViewingPerformance(null)}
        />
      )}
        </>
      )}
    </div>
  );
};

const CreatePromotionModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    promotionType: 'commission_discount' as 'commission_discount' | 'bonus_payout' | 'fixed_bonus',
    providerId: '',
    discountPercentage: '',
    bonusAmount: '',
    minBookingAmount: '',
    zipCodes: '',
    startDate: '',
    endDate: '',
    isActive: true,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await promotionManagementService.createPromotion({
        title: formData.title,
        description: formData.description || undefined,
        promotionType: formData.promotionType,
        providerId: formData.providerId || undefined,
        discountPercentage: formData.promotionType === 'commission_discount' && formData.discountPercentage
          ? parseFloat(formData.discountPercentage)
          : undefined,
        bonusAmount: (formData.promotionType === 'bonus_payout' || formData.promotionType === 'fixed_bonus') && formData.bonusAmount
          ? parseFloat(formData.bonusAmount)
          : undefined,
        minBookingAmount: formData.minBookingAmount ? parseFloat(formData.minBookingAmount) : undefined,
        zipCodes: formData.zipCodes
          ? formData.zipCodes.split(/[,\s]+/).map(s => s.trim()).filter(Boolean)
          : undefined,
        startDate: formData.startDate,
        endDate: formData.endDate,
        isActive: formData.isActive,
      });
      onClose();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to create promotion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>Create Promotion</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Promotion Type *</label>
            <select
              value={formData.promotionType}
              onChange={(e) => setFormData({ ...formData, promotionType: e.target.value as 'commission_discount' | 'bonus_payout' | 'fixed_bonus' })}
              required
            >
              <option value="commission_discount">Commission Discount</option>
              <option value="bonus_payout">Bonus Payout</option>
              <option value="fixed_bonus">Fixed Bonus</option>
            </select>
          </div>
          {formData.promotionType === 'commission_discount' && (
            <div className="form-group">
              <label>Discount Percentage (%) *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.discountPercentage}
                onChange={(e) => setFormData({ ...formData, discountPercentage: e.target.value })}
                required
              />
            </div>
          )}
          {(formData.promotionType === 'bonus_payout' || formData.promotionType === 'fixed_bonus') && (
            <div className="form-group">
              <label>Bonus Amount (₹) *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.bonusAmount}
                onChange={(e) => setFormData({ ...formData, bonusAmount: e.target.value })}
                required
              />
            </div>
          )}
          <div className="form-group">
            <label>Provider ID (leave empty for all providers)</label>
            <input
              type="text"
              value={formData.providerId}
              onChange={(e) => setFormData({ ...formData, providerId: e.target.value })}
              placeholder="provider-id-here"
            />
          </div>
          <div className="form-group">
            <label>Zip Scope (comma-separated, empty = all)</label>
            <input
              type="text"
              value={formData.zipCodes}
              onChange={(e) => setFormData({ ...formData, zipCodes: e.target.value })}
              placeholder="560001, 560002, 560003"
            />
          </div>
          <div className="form-group">
            <label>Min Booking Amount (₹)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.minBookingAmount}
              onChange={(e) => setFormData({ ...formData, minBookingAmount: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Start Date *</label>
            <input
              type="datetime-local"
              value={formData.startDate}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>End Date *</label>
            <input
              type="datetime-local"
              value={formData.endDate}
              onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
              Active
            </label>
          </div>
          <div className="modal-actions">
            <button type="button" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" disabled={loading}>
              {loading ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditPromotionModal: React.FC<{ promotion: Promotion; onClose: () => void }> = ({ promotion, onClose }) => {
  const formatDateForInput = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toISOString().slice(0, 16);
    } catch {
      return dateString;
    }
  };

  const [formData, setFormData] = useState({
    title: promotion.title,
    description: promotion.description || '',
    promotionType: promotion.promotionType,
    providerId: promotion.providerId || '',
    discountPercentage: promotion.discountPercentage?.toString() || '',
    bonusAmount: promotion.bonusAmount?.toString() || '',
    minBookingAmount: promotion.minBookingAmount?.toString() || '',
    zipCodes: promotion.zipCodes?.join(', ') || '',
    startDate: formatDateForInput(promotion.startDate),
    endDate: formatDateForInput(promotion.endDate),
    isActive: promotion.isActive,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await promotionManagementService.updatePromotion(promotion.promotionId, {
        title: formData.title,
        description: formData.description || undefined,
        promotionType: formData.promotionType,
        providerId: formData.providerId || undefined,
        discountPercentage: formData.promotionType === 'commission_discount' && formData.discountPercentage
          ? parseFloat(formData.discountPercentage)
          : undefined,
        bonusAmount: (formData.promotionType === 'bonus_payout' || formData.promotionType === 'fixed_bonus') && formData.bonusAmount
          ? parseFloat(formData.bonusAmount)
          : undefined,
        minBookingAmount: formData.minBookingAmount ? parseFloat(formData.minBookingAmount) : undefined,
        zipCodes: formData.zipCodes
          ? formData.zipCodes.split(/[,\s]+/).map(s => s.trim()).filter(Boolean)
          : undefined,
        startDate: formData.startDate ? new Date(formData.startDate).toISOString() : promotion.startDate,
        endDate: formData.endDate ? new Date(formData.endDate).toISOString() : promotion.endDate,
        isActive: formData.isActive,
      });
      onClose();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to update promotion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>Edit Promotion: {promotion.title}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Promotion Type *</label>
            <select
              value={formData.promotionType}
              onChange={(e) => setFormData({ ...formData, promotionType: e.target.value as 'commission_discount' | 'bonus_payout' | 'fixed_bonus' })}
              required
            >
              <option value="commission_discount">Commission Discount</option>
              <option value="bonus_payout">Bonus Payout</option>
              <option value="fixed_bonus">Fixed Bonus</option>
            </select>
          </div>
          {formData.promotionType === 'commission_discount' && (
            <div className="form-group">
              <label>Discount Percentage (%) *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.discountPercentage}
                onChange={(e) => setFormData({ ...formData, discountPercentage: e.target.value })}
                required
              />
            </div>
          )}
          {(formData.promotionType === 'bonus_payout' || formData.promotionType === 'fixed_bonus') && (
            <div className="form-group">
              <label>Bonus Amount (₹) *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.bonusAmount}
                onChange={(e) => setFormData({ ...formData, bonusAmount: e.target.value })}
                required
              />
            </div>
          )}
          <div className="form-group">
            <label>Provider ID (leave empty for all providers)</label>
            <input
              type="text"
              value={formData.providerId}
              onChange={(e) => setFormData({ ...formData, providerId: e.target.value })}
              placeholder="provider-id-here"
            />
          </div>
          <div className="form-group">
            <label>Zip Scope (comma-separated, empty = all)</label>
            <input
              type="text"
              value={formData.zipCodes}
              onChange={(e) => setFormData({ ...formData, zipCodes: e.target.value })}
              placeholder="560001, 560002, 560003"
            />
          </div>
          <div className="form-group">
            <label>Min Booking Amount (₹)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.minBookingAmount}
              onChange={(e) => setFormData({ ...formData, minBookingAmount: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Start Date *</label>
            <input
              type="datetime-local"
              value={formData.startDate}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>End Date *</label>
            <input
              type="datetime-local"
              value={formData.endDate}
              onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
              Active
            </label>
          </div>
          <div className="modal-actions">
            <button type="button" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" disabled={loading}>
              {loading ? 'Updating...' : 'Update'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const PromotionPerformanceModal: React.FC<{ promotionId: string; onClose: () => void }> = ({ promotionId, onClose }) => {
  const [performance, setPerformance] = useState<{ promotion: Promotion; performance: PromotionPerformance } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPerformance = async () => {
      try {
        setLoading(true);
        const data = await promotionManagementService.getPromotionPerformance(promotionId);
        setPerformance(data);
      } catch (err: unknown) {
        alert(err instanceof Error ? err.message : 'Failed to load performance data');
        onClose();
      } finally {
        setLoading(false);
      }
    };
    loadPerformance();
  }, [promotionId, onClose]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>Promotion Performance</h2>
        {loading ? (
          <div className="loading">Loading performance data...</div>
        ) : performance ? (
          <div className="performance-content">
            <div className="performance-section">
              <h3>{performance.promotion.title}</h3>
              <p>{performance.promotion.description}</p>
            </div>
            <div className="performance-stats">
              <div className="performance-stat">
                <div className="stat-label">Status</div>
                <div className="stat-value">{performance.performance.status}</div>
              </div>
              <div className="performance-stat">
                <div className="stat-label">Affected Providers</div>
                <div className="stat-value">{performance.performance.affectedProvidersCount}</div>
              </div>
              <div className="performance-stat">
                <div className="stat-label">Estimated Benefit</div>
                <div className="stat-value">₹{performance.performance.estimatedBenefit.toLocaleString()}</div>
              </div>
              <div className="performance-stat">
                <div className="stat-label">Estimated Bookings</div>
                <div className="stat-value">{performance.performance.estimatedBookings}</div>
              </div>
            </div>
            <div className="performance-dates">
              <div>Start Date: {new Date(performance.performance.startDate).toLocaleString()}</div>
              <div>End Date: {new Date(performance.performance.endDate).toLocaleString()}</div>
            </div>
          </div>
        ) : null}
        <div className="modal-actions">
          <button type="button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
